/**
 * DocSection
 *
 * Anchor-targetable section wrapper. The id attribute is what the sticky
 * Table-of-Contents scrolls to, and what the IntersectionObserver watches
 * to highlight the active TOC entry.
 */
import type { ReactNode } from 'react';
import { cn } from '@respectly/ui';

export interface DocSectionProps {
  id: string;
  title: string;
  eyebrow?: string;
  children: ReactNode;
  className?: string;
}

export function DocSection({
  id,
  title,
  eyebrow,
  children,
  className,
}: DocSectionProps) {
  return (
    <section
      id={id}
      data-doc-section={id}
      className={cn('scroll-mt-24 pb-12 border-b border-slate-200/60 dark:border-slate-800/60 last:border-b-0', className)}
    >
      {eyebrow ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="mb-4 text-2xl font-bold text-slate-900 dark:text-white">
        <a href={`#${id}`} className="hover:underline">
          {title}
        </a>
      </h2>
      <div className="prose-doc max-w-none space-y-4 text-[15px] leading-7 text-slate-700 dark:text-slate-300">
        {children}
      </div>
    </section>
  );
}
